import type { RowData, Table as TanStackTable } from "@tanstack/react-table";
import type { Ref } from "react";
import { cn } from "../../lib/utils";
import { structuralRowColorVariants, structuralRowTextVariants } from "./structuralRowStyles";
import { useTableContext } from "./TableContext";
import { TableHeaderCell } from "./TableHeaderCell";
import { TableSelectionCell } from "./TableSelectionCell";

export interface TableHeaderProps<TRow extends RowData> {
  table: TanStackTable<TRow>;
  /** The header's own grid, measured for column resizing. */
  ref: Ref<HTMLDivElement>;
  /** The clipping wrapper whose horizontal scroll position follows the body. */
  viewportRef: Ref<HTMLDivElement>;
  /** Width of the body's vertical scrollbar, reserved so header and body columns stay aligned. */
  scrollbarGutter: number;
  minWidth: number;
}

export const TableHeader = <TRow extends RowData>({
  table,
  ref,
  viewportRef,
  scrollbarGutter,
  minWidth,
}: TableHeaderProps<TRow>) => {
  const { gridTemplateColumns, canReorder, canSelect, structuralRowsColor, structuralRowsText } = useTableContext();

  return (
    <div
      ref={viewportRef}
      data-slot="table-header-viewport"
      className={cn("shrink-0 overflow-hidden border-b", structuralRowColorVariants({ color: structuralRowsColor }))}
      style={{ paddingRight: scrollbarGutter }}
    >
      <div
        ref={ref}
        data-slot="table-header"
        data-testid="table-header"
        className={cn(
          "grid h-12 items-center px-4 font-medium",
          structuralRowTextVariants({ text: structuralRowsText })
        )}
        style={{ gridTemplateColumns, minWidth }}
      >
        {canReorder && <div />}
        {canSelect && <TableSelectionCell />}
        {table.getVisibleLeafColumns().map((column) => (
          <TableHeaderCell key={column.id} column={column} />
        ))}
      </div>
    </div>
  );
};
